import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloudinaryImage, Gallery } from '../../types/Gallery';
import { getGalleryById, loadLiveGalleries } from '../../utils/GalleryAPI';

export type RowArrangement = 'editorial' | 'equal';

export interface PickedPhoto {
  src: string;
  publicId?: string;
  alt: string;
  caption: string;
  width?: number;
  height?: number;
}

export type MasonryColumns = 1 | 2 | 3 | 4 | 5;

export const MASONRY_COLUMN_OPTIONS: MasonryColumns[] = [1, 2, 3, 4, 5];

export const MASONRY_COLUMN_COPY: Record<MasonryColumns, string> = {
  1: 'One column. One photograph sits on its own. Several stack at full width.',
  2: 'Each photograph is about half the article width.',
  3: 'A comfortable size for a group of photographs.',
  4: 'Each photograph is smaller.',
  5: 'Five across. Each photograph is the smallest.',
};

export function asMasonryColumns(value: number): MasonryColumns {
  if (value === 1 || value === 2 || value === 3 || value === 4 || value === 5) return value;
  return 3;
}

interface BlogPhotoPickerProps {
  count: 1 | 2 | 3;
  arrangement: RowArrangement;
  onArrangement: (value: RowArrangement) => void;
  onClose: () => void;
  onInsert: (photos: PickedPhoto[]) => void;
  requireLandscape?: boolean;
  masonry?: boolean;
  columns?: MasonryColumns;
  onColumns?: (value: MasonryColumns) => void;
  selectionLimit?: number;
}

type PhotoShape = 'portrait' | 'landscape' | 'square';

export function photoShape(width?: number, height?: number): PhotoShape | null {
  if (!width || !height) return null;
  const ratio = width / height;
  if (ratio > 1.05) return 'landscape';
  if (ratio < 0.95) return 'portrait';
  return 'square';
}

const SHAPE_LABEL: Record<PhotoShape, string> = {
  portrait: 'Portrait',
  landscape: 'Landscape',
  square: 'Square',
};

type Step = 'galleries' | 'photos' | 'details';

function photoUrl(image: CloudinaryImage): string {
  const url = image.secure_url || image.url || '';
  return /^https:\/\//i.test(url) ? url : '';
}

function fileName(image: CloudinaryImage): string {
  return image.filename || image.publicId || image.public_id || '';
}

function savedAlt(image: CloudinaryImage): string {
  return (image.alt || image.context?.alt || '').trim();
}

function savedCaption(image: CloudinaryImage): string {
  return (image.context?.caption || '').trim();
}

function BlogPhotoPicker({
  count,
  arrangement,
  onArrangement,
  onClose,
  onInsert,
  requireLandscape = false,
  masonry = false,
  columns = 3,
  onColumns,
  selectionLimit,
}: BlogPhotoPickerProps) {
  const [step, setStep] = useState<Step>('galleries');
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [gallery, setGallery] = useState<Gallery | null>(null);
  const [photos, setPhotos] = useState<CloudinaryImage[]>([]);
  const [selected, setSelected] = useState<PickedPhoto[]>([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [measured, setMeasured] = useState<Record<string, PhotoShape>>({});

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadLiveGalleries('cards').then((loaded) => {
      if (cancelled) return;
      setGalleries(loaded.filter((item) => item.published !== false));
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setError('The galleries could not be loaded.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const names = new Set<string>();
    galleries.forEach((item) => { if (item.category) names.add(item.category); });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [galleries]);

  const query = search.trim().toLowerCase();
  const visibleGalleries = galleries.filter((item) => {
    if (category !== 'all' && item.category !== category) return false;
    return !query || item.name.toLowerCase().includes(query);
  });
  const visiblePhotos = photos.filter((image) => {
    if (!photoUrl(image)) return false;
    if (!query) return true;
    const haystack = [savedAlt(image), savedCaption(image), fileName(image)].join(' ').toLowerCase();
    return haystack.includes(query);
  });

  const openGallery = (item: Gallery) => {
    setSearch('');
    setError('');
    setLoading(true);
    setGallery(item);
    setStep('photos');
    getGalleryById(String(item.id)).then((full) => {
      const images = (full?.images || []).filter((image) => photoUrl(image));
      setPhotos(images);
      if (!images.length) setError('This gallery has no photographs to choose.');
      setLoading(false);
    }).catch(() => {
      setPhotos([]);
      setError('This gallery could not be opened.');
      setLoading(false);
    });
  };

  const shapeOf = (image: CloudinaryImage) => {
    const src = photoUrl(image);
    return (src && measured[src]) || photoShape(image.width, image.height);
  };

  const rememberShape = (src: string, width: number, height: number) => {
    const shape = photoShape(width, height);
    if (!src || !shape) return;
    setMeasured((current) => (current[src] === shape ? current : { ...current, [src]: shape }));
    if (requireLandscape && shape !== 'landscape') {
      setSelected((current) => current.some((item) => item.src === src)
        ? current.filter((item) => item.src !== src)
        : current);
      return;
    }
    setSelected((current) => current.map((item) => (
      item.src === src ? { ...item, width: item.width || width, height: item.height || height } : item
    )));
  };

  const togglePhoto = (image: CloudinaryImage) => {
    const src = photoUrl(image);
    if (!src) return;
    const existing = selected.findIndex((item) => item.src === src);
    if (existing >= 0) {
      setSelected(selected.filter((item) => item.src !== src));
      setError('');
      return;
    }
    const shape = shapeOf(image);
    if (requireLandscape && shape && shape !== 'landscape') {
      setError(shape === 'portrait'
        ? 'That photograph is portrait. The featured image needs to be wider than it is tall.'
        : 'That photograph is square. The featured image needs to be wider than it is tall.');
      return;
    }
    const cap = selectionLimit || (masonry ? 12 : count);
    if (selected.length >= cap) return;
    setError('');
    const alt = savedAlt(image);
    const caption = savedCaption(image);
    setSelected([...selected, {
      src,
      publicId: image.publicId || image.public_id || '',
      alt: alt || (caption && caption.toLowerCase() !== (gallery?.name || '').toLowerCase() ? caption : ''),
      caption: alt && caption && caption !== alt ? caption : '',
      width: image.width,
      height: image.height,
    }]);
  };

  const updatePhoto = (src: string, field: 'alt' | 'caption', value: string) => {
    setSelected(selected.map((item) => (item.src === src ? { ...item, [field]: value } : item)));
  };

  const landscapeReady = !requireLandscape || selected.every((item) => (measured[item.src] || photoShape(item.width, item.height)) === 'landscape');
  const enough = selectionLimit
    ? selected.length >= 1 && selected.length <= selectionLimit
    : (masonry ? selected.length >= 1 && selected.length <= 12 : selected.length === count);
  const ready = enough && selected.every((item) => item.alt.trim()) && landscapeReady;
  const title = requireLandscape
    ? 'Choose a landscape photograph'
    : (selectionLimit ? 'Add photographs' : (masonry ? 'Choose photographs' : (count === 1 ? 'Choose a photograph' : `Choose ${count} photographs`)));

  return createPortal(
    <div className="blog-picker-backdrop blog-picker-front" role="presentation">
      <div className="blog-picker" role="dialog" aria-modal="true" aria-label={title}>
        <header className="blog-picker-head">
          <div>
            <p className="blog-picker-kicker">From your galleries</p>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onClose}>Close</button>
        </header>
        <p className="blog-picker-note">
          {requireLandscape
            ? 'The original photograph is used. Pick a landscape one — wider than it is tall.'
            : selectionLimit
              ? 'Pick one or more. They are added to this group.'
              : masonry
                ? 'Pick one or more photographs, up to twelve. One column is full width. More columns make each photograph smaller.'
                : 'The original photograph is used. Nothing is uploaded again.'}
        </p>
        {step !== 'details' ? (
          <div className="blog-picker-tools">
            <input
              value={search}
              placeholder={step === 'galleries' ? 'Search galleries' : 'Search photographs'}
              onChange={(event) => setSearch(event.target.value)}
            />
            {step === 'galleries' && categories.length > 1 ? (
              <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Gallery category">
                <option value="all">All categories</option>
                {categories.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            ) : null}
          </div>
        ) : null}
        {error ? <p className="blog-picker-error">{error}</p> : null}
        {loading ? <p className="blog-picker-note">Loading…</p> : null}

        {step === 'galleries' && !loading ? (
          <ul className="blog-picker-galleries">
            {visibleGalleries.map((item) => {
              const thumb = item.images?.[0] || item.heroImage;
              const src = thumb ? photoUrl(thumb) : '';
              return (
                <li key={item.id}>
                  <button type="button" onClick={() => openGallery(item)}>
                    {src ? <img src={src} alt="" /> : <span className="blog-picker-empty" aria-hidden="true" />}
                    <span className="blog-picker-gallery-copy">
                      <strong>{item.name}</strong>
                      <em>{[item.category ? item.category.replace(/-/g, ' ') : '', `${item.imageCount || item.images?.length || 0} photographs`].filter(Boolean).join(' · ')}</em>
                    </span>
                  </button>
                </li>
              );
            })}
            {!visibleGalleries.length ? <li className="blog-picker-empty-copy">No galleries match that search.</li> : null}
          </ul>
        ) : null}

        {step === 'photos' && !loading ? (
          <>
            <div className="blog-picker-bar">
              <button type="button" onClick={() => { setStep('galleries'); setSearch(''); setError(''); }}>Back to galleries</button>
              <span>{masonry ? `${selected.length} selected` : `${selected.length} of ${count} selected`}</span>
            </div>
            <ul className="blog-picker-photos">
              {visiblePhotos.map((image) => {
                const src = photoUrl(image);
                const index = selected.findIndex((item) => item.src === src);
                const shape = shapeOf(image);
                const name = savedAlt(image) || savedCaption(image) || fileName(image) || 'Photograph';
                return (
                  <li key={src || fileName(image)}>
                    <button
                      type="button"
                      className={index >= 0 ? 'is-selected' : ''}
                      aria-pressed={index >= 0}
                      aria-label={shape ? `${name}, ${SHAPE_LABEL[shape]}` : name}
                      onClick={() => togglePhoto(image)}
                    >
                      <img
                        src={src}
                        alt=""
                        onLoad={(event) => {
                          const photo = event.currentTarget;
                          if (photo.naturalWidth > 0 && photo.naturalHeight > 0) {
                            rememberShape(src, image.width || photo.naturalWidth, image.height || photo.naturalHeight);
                          }
                        }}
                      />
                      {shape ? (
                        <span className={`blog-orient is-${shape}`}>
                          <i aria-hidden="true" />
                          {SHAPE_LABEL[shape]}
                        </span>
                      ) : null}
                      {index >= 0 ? <span className="blog-picker-badge">{index + 1}</span> : null}
                      <span>{name}</span>
                    </button>
                  </li>
                );
              })}
              {!visiblePhotos.length ? <li className="blog-picker-empty-copy">No photographs match that search.</li> : null}
            </ul>
            <footer className="blog-picker-foot">
              <button type="button" className="btn btn-primary" disabled={!enough || !landscapeReady} onClick={() => { setStep('details'); setError(''); }}>
                Continue
              </button>
            </footer>
          </>
        ) : null}

        {step === 'details' ? (
          <>
            <div className="blog-picker-bar">
              <button type="button" onClick={() => setStep('photos')}>Back to photographs</button>
            </div>
            {masonry && !selectionLimit ? (
              <fieldset className="blog-arrangement">
                <legend>Columns</legend>
                {MASONRY_COLUMN_OPTIONS.map((value) => (
                  <button key={value} type="button" className={columns === value ? 'is-selected' : ''} onClick={() => onColumns?.(value)}>
                    {value === 1 ? '1 column' : `${value} columns`}
                  </button>
                ))}
                <p>{MASONRY_COLUMN_COPY[columns]}</p>
              </fieldset>
            ) : count > 1 ? (
              <fieldset className="blog-arrangement">
                <legend>Arrangement</legend>
                <button type="button" className={arrangement === 'editorial' ? 'is-selected' : ''} onClick={() => onArrangement('editorial')}>
                  <span className={`blog-arrangement-mark is-${count === 3 ? 'trio' : 'pair'}`} aria-hidden="true"><i /><i />{count === 3 ? <i /> : null}</span>
                  Editorial
                </button>
                <button type="button" className={arrangement === 'equal' ? 'is-selected' : ''} onClick={() => onArrangement('equal')}>
                  <span className="blog-arrangement-mark is-equal" aria-hidden="true"><i /><i />{count === 3 ? <i /> : null}</span>
                  Equal width
                </button>
                <p>{arrangement === 'editorial' ? 'One photograph is a little larger. This is the usual look.' : 'Every photograph is the same width.'}</p>
              </fieldset>
            ) : null}
            <ol className="blog-picker-details">
              {selected.map((photo) => (
                <li key={photo.src}>
                  <img src={photo.src} alt="" />
                  <label>Description
                    <input value={photo.alt} onChange={(event) => updatePhoto(photo.src, 'alt', event.target.value)} />
                  </label>
                  <label>Caption, optional
                    <input value={photo.caption} placeholder="Shown under this photograph" onChange={(event) => updatePhoto(photo.src, 'caption', event.target.value)} />
                  </label>
                </li>
              ))}
            </ol>
            {!ready ? (
              <p className="blog-picker-note">
                {requireLandscape && !landscapeReady
                  ? 'The featured photograph needs to be landscape.'
                  : 'Each photograph needs a short description.'}
              </p>
            ) : null}
            <footer className="blog-picker-foot">
              <button type="button" className="btn btn-primary" disabled={!ready} onClick={() => onInsert(selected.map((item) => ({ ...item, alt: item.alt.trim(), caption: item.caption.trim() })))}>
                {requireLandscape ? 'Use as featured image' : (selectionLimit ? 'Add to this group' : 'Add to article')}
              </button>
            </footer>
          </>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export default BlogPhotoPicker;
